import { CaseData, PropertyCardProps } from "@/cms/models";
import {
  formatNumber,
  getCaseClassification,
  getCaseUrl,
  getLabel,
  isAgricultural,
  isCommercial,
  isCountryEstate,
  isHomeSale,
  isInvestmentProperty,
  isLand,
  isOwnProperty,
  isRental,
  squareMetersToHectares,
} from "@/utils/helper";
import Link from "next/link";
import { useState } from "react";
import { SpinnerSvg } from "./icons/Spinner";
import Card from "./ui/Card";

const PropertyGalleryCard: React.FC<PropertyCardProps> = ({ property }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const imageUrl = property.images.find((x) => x.mdUrl)?.mdUrl;
  const label = getLabel(property);

  const classifications = getCaseClassification(property);

  return (
    <Link
      aria-label={property.displayName}
      onClick={() => setIsLoading(true)}
      href={getCaseUrl(property)}
      className="flex flex-col relative h-full justify-end"
    >
      <Card>
        {isLoading && (
          <div className="absolute flex justify-center items-center inset-0 bg-gray-800 opacity-50 transition-opacity z-20">
            <SpinnerSvg />
          </div>
        )}

        <div className="overflow-hidden">
          <div className={`w-full relative ${label ? "min-h-8" : ""}`}>
            <div className="cursor-pointer hover:scale-110 transform duration-300 h-[210px] w-full  relative">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={property.displayName}
                  className=" object-cover h-full w-full hover:scale-110 aspect-[16/9]"
                />
              ) : (
                <div className="flex items-center justify-center h-full w-full bg-gray-200 aspect-[16/9]">
                  <span className="text-gray-500 text-lg">
                    Ingen billede tilgængeligt
                  </span>
                </div>
              )}
            </div>
            {label && (
              <div className="absolute homepage-text-sm top-0 left-0 uppercase bg-[var(--label-bg-color)] text-[var(--label-text-color)] px-4 py-2 text-xs">
                <span>{label}</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 pb-0 flex flex-col homepage-text-sm">
          <div className="h-[56px]">
            <div className="line-clamp-1 homepage-text-lg">
              {property.shortAddress} {property.supplementaryCityName}
            </div>
            <div className="line-clamp-1 text-header-level-3">
              {property.location}
            </div>
          </div>

          {getPropertyFactsView(property, classifications)}
        </div>

        <div className="p-4">
          <div className=" border-t border-gray-300">
            <div className="mt-4 flex justify-end homepage-text-lg">
              <div>{property.priceInfo}</div>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

function getPropertyFactsView(property: CaseData, list: string[]) {
  // Grunde
  if (isLand(list)) {
    return <PlotOfLandPropertyFacts property={property} />;
  }

  // Erhverv | Investering
  if (isCommercial(list) && isInvestmentProperty(list)) {
    return <CommercialInvestmentPropertyFacts property={property} />;
  }

  // Erhverv | Eget brug (Køb)
  if (isHomeSale(list) && isCommercial(list) && isOwnProperty(list)) {
    return <CommercialOwnUseBoughtPropertyFacts property={property} />;
  }

  // Erhverv | Eget brug (Leje)
  if (isRental(list) && isCommercial(list) && isOwnProperty(list)) {
    return <CommercialOwnUseRentalPropertyFacts property={property} />;
  }

  // Landejendomme
  // Landbrug | Lystejendom
  if (isAgricultural(list) || isCountryEstate(list)) {
    return <AgriculturalPropertyFacts property={property} />;
  }

  // Fallback
  return <DefaultPropertyFacts property={property} />;
}

interface PropertyFactProps {
  property: CaseData;
}

const DefaultPropertyFacts: React.FC<PropertyFactProps> = ({ property }) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Boligareal</p>
          <p>
            {property.housingArea > 0 ? (
              <span>
                {formatNumber(property.housingArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Værelser</p>
          <p>
            {property.numberOfRooms > 0 ? (
              <span>{formatNumber(property.numberOfRooms)}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

// Grunde
const PlotOfLandPropertyFacts: React.FC<PropertyFactProps> = ({ property }) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Grundareal</p>
          <p>
            {property.plotArea > 0 ? (
              <span>
                {formatNumber(property.plotArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

// Erhverv | Investering
const CommercialInvestmentPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Etageareal</p>
          <p>
            {property.floorArea > 0 ? (
              <span>
                {formatNumber(property.floorArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Afkast i %</p>
          <p>
            {property.rateOfReturn > 0 ? (
              <span>
                {formatNumber(property.rateOfReturn, {
                  fragments: 1,
                })}
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};
// Erhverv | Eget brug (Køb)
const CommercialOwnUseBoughtPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Etageareal</p>
          <p>
            {property.floorArea > 0 ? (
              <span>
                {formatNumber(property.floorArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Driftsudgifter</p>
          <p>
            {property.operatingCostsAnnually > 0 ? (
              <span>{formatNumber(property.operatingCostsAnnually)}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

// Erhverv | Eget brug (Leje)
const CommercialOwnUseRentalPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Etageareal</p>
          <p>
            {property.floorArea > 0 ? (
              <span>
                {formatNumber(property.floorArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Driftsudgifter</p>
          <p>
            {property.operatingCostsAnnually > 0 ? (
              <span>{formatNumber(property.operatingCostsAnnually)}</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

// Landejendomme
// Landbrug | Lystejendom
const AgriculturalPropertyFacts: React.FC<PropertyFactProps> = ({
  property,
}) => {
  return (
    <ul className="w-full mt-4 text-base">
      <li>
        <div className="flex justify-between">
          <p>Boligareal</p>
          <p>
            {property.housingArea > 0 ? (
              <span>
                {formatNumber(property.housingArea)} m<sup>2</sup>
              </span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
      <li>
        <div className="flex justify-between">
          <p>Grundareal</p>
          <p>
            {property.plotArea > 0 ? (
              <span>{squareMetersToHectares(property.plotArea)} ha</span>
            ) : (
              <span>-</span>
            )}
          </p>
        </div>
      </li>
    </ul>
  );
};

export default PropertyGalleryCard;
